export const ORDER_PICK_SCAN_BROADCAST_EVENT = 'order_pick_scan';
export const ORDER_PICK_SCANS_CLEARED_EVENT = 'order_pick_scans_cleared';

export interface OrderPickScanLine {
  readonly lineId: string;
  readonly sku: string | null;
  readonly storageIdPrefix: string | null;
}

export function orderPickScanRealtimeTopic(orderId: string): string {
  return `order-pick-scans-${orderId}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readId(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null;
}

function readNullableId(value: unknown): string | null {
  if (value == null) return null;
  return readId(value);
}

function scanLine(row: Record<string, unknown>): OrderPickScanLine | null {
  const lineId = readId(row.line_id) ?? readId(row.lineId);
  if (!lineId) return null;
  return {
    lineId,
    sku: readNullableId(row.sku),
    storageIdPrefix: readNullableId(row.storage_id_prefix) ?? readNullableId(row.storageIdPrefix),
  };
}

function messageBody(raw: unknown): Record<string, unknown> | null {
  if (!isRecord(raw)) return null;
  const payload = isRecord(raw.payload) ? raw.payload : raw;
  if (typeof payload.event === 'string' && isRecord(payload.payload)) {
    return payload.payload;
  }
  return payload;
}

/**
 * Read scan lines from a Supabase Realtime callback.
 * Postgres inserts use `{ new: { order_id, line_id, sku, storage_id_prefix } }`.
 * Broadcasts use `{ payload: { orderId, lines } }`.
 */
export function parseOrderPickScanMessage(
  raw: unknown,
): { readonly orderId: string; readonly lines: readonly OrderPickScanLine[] } | null {
  const body = messageBody(raw);
  if (!body) return null;

  const row = isRecord(body.new) ? body.new : body;
  const orderId = readId(row.order_id) ?? readId(row.orderId);
  if (!orderId) return null;

  if (Array.isArray(row.lines)) {
    const lines: OrderPickScanLine[] = [];
    for (const entry of row.lines) {
      if (!isRecord(entry)) continue;
      const line = scanLine(entry);
      if (line) lines.push(line);
    }
    if (lines.length === 0) return null;
    return { orderId, lines };
  }

  const line = scanLine(row);
  if (!line) return null;
  return { orderId, lines: [line] };
}

/** Read a reset broadcast or a postgres DELETE of one scan row. */
export function parseOrderPickScanRemoval(
  raw: unknown,
): { readonly orderId: string; readonly lineId: string | null } | null {
  const body = messageBody(raw);
  if (!body) return null;
  const row = isRecord(body.old) ? body.old : body;
  const orderId = readId(row.order_id) ?? readId(row.orderId);
  if (!orderId) return null;
  const lineId = readId(row.line_id) ?? readId(row.lineId);
  return { orderId, lineId };
}
